import { Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatStepper } from '@angular/material/stepper';

import { Header } from '@domain/interface/header';
import { CsvUtilService } from '@service/csv-util/csv-util.service';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { FirstStepComponent } from '../first-step/first-step.component';
import { environment } from '@environment/environment';
import { SecondStepComponent } from '../second-step/second-step.component';
import { IS_EMPTY, IS_NOT_NULL_OR_UNDEFINED, RestService } from 'cargo-chief-commons';
import { ThirdStepComponent } from '../third-step/third-step.component';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { UrlService } from '@service/url/url.service';
import { ConfirmationDialogComponent } from '../../../common/confirmation-dialog/confirmation-dialog.component';
import {
	GET_FILE_DATA_VALIDATOR,
	GET_FORM_GROUP,
	GET_HEADERS_MAP,
	GET_REQUIRED_HEADERS,
	IS_EMPTY_LINE,
} from '@service/csv-util/shared/utils';
import { UserSettingsService } from '@service/userSettings/user-settings.service';
import { LoginSetupService } from '@service/login-setup/login-setup.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { ProductsService } from '@service/products/products.service';
import { BookingAssistantService } from '@service/booking-assistant/booking-assistant.service';

@Component({
	selector: 'app-upload-csv-dialog',
	templateUrl: './upload-csv-dialog.component.html',
	styleUrls: ['./upload-csv-dialog.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class UploadCsvDialogComponent implements OnInit {
	@ViewChild('stepper') stepper: MatStepper;
	@ViewChild(FirstStepComponent) uploadFileComponent: FirstStepComponent;
	@ViewChild('mapFields') mapFieldsComponent: SecondStepComponent;
	@ViewChild('preview') previewComponent: ThirdStepComponent;

	public expectedHeadersMap: Header[] = [];
	public requiredHeaders: string[] = [];
	public expectedHeaders = [];
	public fileDataValidator: Function;
	public formGroupObject: UntypedFormGroup;
	public fileName = '';
	public headersDropdownList = [];
	public csvFileData = [];
	public allLines = [];
	public newHeadersObject = {};
	public showPreview = false;
	public dataType;
	public title = 'Upload CSV file';
	subtitle: string = '';

	secondStepTitle: string = '';
	thirdStepTitle: string = '';

	errorMsgs: Array<string> = [];

	currentCarrierOffersStatus: boolean = false;

	constructor(
		private dialogRef: MatDialogRef<UploadCsvDialogComponent>,
		private csvUtilService: CsvUtilService,
		private utilService: UtilService,
		@Inject(MAT_DIALOG_DATA) public dialogData: any,
		public accountSettings: AccountSettingsService,
		public dialog: MatDialog,
		private urlService: UrlService,
		private restService: RestService,
		private userSettings: UserSettingsService,
		private loginSetupService: LoginSetupService,
		public cargochief: Cargochief,
		public productsService: ProductsService,
		public bookingAssistantService: BookingAssistantService
	) {
		let dataType;
		this.dataType = dataType = dialogData.dataType;
		this.title = dialogData.title;
		this.subtitle = this.dialogData.subtitle;
		this.secondStepTitle = this.dialogData.secondStepTitle;
		this.thirdStepTitle = this.dialogData.thirdStepTitle;

		if (IS_EMPTY(this.dialogData.secondStepTitle)) {
			this.secondStepTitle = this.title;
		}
		if (IS_EMPTY(this.dialogData.thirdStepTitle)) {
			this.thirdStepTitle = this.title;
		}

		this.expectedHeadersMap = GET_HEADERS_MAP(this.csvUtilService.dataMap, dataType);
		this.requiredHeaders = GET_REQUIRED_HEADERS(this.csvUtilService.dataMap, dataType);
		this.fileDataValidator = GET_FILE_DATA_VALIDATOR(this.csvUtilService.dataMap, dataType);
		this.formGroupObject = GET_FORM_GROUP(this.csvUtilService.dataMap, dataType);
		this.initialiseExpectedHeaders();
	}

	ngOnInit() {
		this.setCurrentCarrierOfferStatus();
	}

	initialiseExpectedHeaders() {
		let self = this;
		this.expectedHeadersMap.forEach((map: Header) => {
			self.expectedHeaders.push(map.value);
		});
	}

	afterFirstStep({ fileData, fileName }) {
		setTimeout(() => {
			this.allLines = fileData;
			this.fileName = fileName;
			this.formGroupObject = GET_FORM_GROUP(this.csvUtilService.dataMap, this.dataType);
			this.stepper.next();
		});
	}

	afterSecondStep(headerRow: string) {
		this.errorMsgs.length = 0;
		this.allLines[0] = headerRow;
		setTimeout(() => {
			let errorMessages = this.fileDataValidator(
				this.allLines.slice(0, 1)[0],
				this.allLines.slice(1),
				this.csvUtilService.dataMap[this.dataType].requiredHeaders
			);
			this.removeEmptyLines();
			if (this.allLines.length < 2) {
				errorMessages = [...errorMessages, 'Empty file cannot be processed'];
			}
			let isFileDataValid = errorMessages.length == 0;
			if (isFileDataValid) {
				//this.buildFileDataForPreview();
				this.stepper.next();
				this.showPreview = true;
			} else {
				errorMessages.forEach((errorMessage: string) => {
					this.utilService.showSnackbar(errorMessage, SNACKBAR.ERROR, SNACKBAR.ACTION);
				});
				this.errorMsgs = errorMessages
					.filter((error, i, ar) => ar.indexOf(error) === i)
					.slice(0, environment.RFP_PREVIEW_TABLE_ROWS_LIMIT);
				this.stepper.next();
			}
		});
	}

	removeEmptyLines(): void {
		let nonEmptyLines = [];
		this.allLines.forEach((line) => {
			if (!IS_EMPTY_LINE(line)) {
				nonEmptyLines.push(line);
			}
		});

		this.allLines = [...nonEmptyLines];
	}

	clearFile() {
		this.csvFileData = [];
		this.headersDropdownList = [];
		this.newHeadersObject = {};
		this.allLines = [];
		this.showPreview = false;
	}

	goBack() {
		this.stepper.previous();
	}

	closeDialog(data: any): void {
		this.clearFile();
		this.dialogRef.close({ fileName: this.fileName, data });
	}

	resetForm() {
		if (this.uploadFileComponent) {
			this.uploadFileComponent.clearFile();
		}
		this.clearFile();
		this.stepper.selectedIndex = 0;
	}

	showActions(): boolean {
		return this.isFirstStep() || this.isSecondStep();
	}

	isFirstStep(): boolean {
		return this.stepper?.selectedIndex == 1 && IS_NOT_NULL_OR_UNDEFINED(this.mapFieldsComponent);
	}

	isSecondStep(): boolean {
		return this.stepper?.selectedIndex == 2 && IS_NOT_NULL_OR_UNDEFINED(this.previewComponent);
	}

	setCurrentCarrierOfferStatus() {
		this.currentCarrierOffersStatus =
			this.accountSettings.BOOKING_ASSISTANT &&
			this.productsService.BOOKING_ASSISTANT &&
			this.userSettings.BOOKING_ASSISTANT;
	}

	carrierOfferStatusChanged() {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '900px',
			data: {
				title: ' ',
				body: !this.currentCarrierOffersStatus
					? 'C4 will not automatically contact matching carriers for these loads'
					: 'C4 will automatically contact matching carriers for these loads',
				cancelButtonText: 'Close',
				ConfirmButtonTest: 'Confirm',
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result == null) {
				this.setCurrentCarrierOfferStatus();
			} else {
				const url = this.urlService.updateUserSettingUrl({
					settingName: 'BOOKING_ASSISTANT',
					value: this.currentCarrierOffersStatus,
				});
				this.restService.PUT(url, {}).subscribe(
					(result) => {
						this.utilService.showSnackbar(
							'Successfully updated the settings',
							SNACKBAR.SUCCESS,
							SNACKBAR.ACTION
						);
						this.loginSetupService.updateLoginSetup();
					},
					(error) => {
						this.utilService.showSnackbar(
							'Error while updating the settings. Try again later.',
							SNACKBAR.ERROR,
							SNACKBAR.ACTION
						);
					}
				);
			}
		});
	}

	isInvalidTrailerType(): boolean {
		return IS_NOT_NULL_OR_UNDEFINED(
			this.previewComponent.errorMsgs.find((error) => error.indexOf('Invalid trailer type') !== -1)
		);
	}
}
